import { useEffect, useMemo, useState } from 'react';
import { adLocksApi, AdLock, AdLockScope, getAdLockClientId, isOwnedByCurrentUser } from './adLocks';

export function useAdScreenLock(scope: AdLockScope, adId?: string) {
    const clientId = useMemo(() => getAdLockClientId(), []);
    const [lockInfo, setLockInfo] = useState<AdLock | null>(null);
    const [lockReady, setLockReady] = useState(false);

    useEffect(() => {
        if (!adId) {
            setLockInfo(null);
            setLockReady(true);
            return;
        }

        let isActive = true;
        let intervalId: number | undefined;

        const syncLock = async () => {
            try {
                const result = await adLocksApi.acquire(scope, adId, clientId);
                if (!isActive) {
                    return;
                }
                setLockInfo(result.lock);
                setLockReady(true);
            } catch (error) {
                if (!isActive) {
                    return;
                }
                console.error('Failed to sync ad lock', error);
                setLockReady(false);
            }
        };

        const releaseLock = () =>
            adLocksApi.release(scope, adId, clientId, { keepalive: true }).catch((error) => {
                console.error('Failed to release ad lock', error);
            });

        setLockReady(false);
        void syncLock();
        intervalId = window.setInterval(syncLock, 5000);

        const handleBeforeUnload = () => {
            void releaseLock();
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            isActive = false;
            if (intervalId) {
                window.clearInterval(intervalId);
            }
            window.removeEventListener('beforeunload', handleBeforeUnload);
            void releaseLock();
        };
    }, [scope, adId, clientId]);

    const lockedByAnotherUser = Boolean(lockInfo && !isOwnedByCurrentUser(lockInfo));

    return {
        lockInfo,
        lockReady,
        lockedByAnotherUser
    };
}
